import React from 'react';
import { withAlert } from 'react-alert';
import { getAlertMessage, getAlertTitle } from '../../../../helpers/Alert';
import { appendAlertHistory } from '../../../../tools/alerts/reducer';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import {
  fetchPersonalAlert,
  fetchSystemAlert,
  REDUCER_NAME,
  updateNotificationList,
} from './reducer';
import { REDUCER_NAME as SIDEBAR_REDUCER } from '../../../../main-layout/sidebar/reducer';
import { connect } from 'react-redux';
import messageHub from '../../../../../core/signalr/SignalrMessageHub';
import { isNotLogin } from '../../../../helpers/Common';

const DELAY_SHOW_MESSAGE = 3500;
export const ALERT_LOCAL_STORAGE_KEY = 'notifications';

class AlertSubscriber extends React.PureComponent {
  messages = [];
  lastShowTime = null;
  isFirstLoad = false;

  componentDidMount() {
    this.fetchData();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  componentDidUpdate(prevProps, prevState) {
    const { settingSystem } = this.props;
    if (
      !settingSystem.isLoading &&
      prevProps.settingSystem.isLoading &&
      !this.isFirstLoad &&
      this.isShowNotification(settingSystem)
    ) {
      this.isFirstLoad = true;
      const messages = this.getMessages();
      if (messages.length) {
        messages.forEach(message => {
          this.onReceiveAlert(message);
        });
      }
    }
    const isShowNotification = settingSystem.dataSystem.notificationSubscribed;
    const prevIsShowNotification =
      prevProps.settingSystem.dataSystem.notificationSubscribed;
    if (!isShowNotification && prevIsShowNotification) {
      clearInterval(this.intervalShowMessage);
      this.intervalShowMessage = null;
      this.messages = [];
      localStorage.removeItem(ALERT_LOCAL_STORAGE_KEY);
    }
    this.handleSubscribe(prevProps);
  }

  componentWillUnmount() {
    this.unsubscribeSystemAlert(this.props);
    this.unsubscribePersonalAlert(this.props);
    clearInterval(this.intervalShowMessage);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  onBeforeUnload = e => {
    e.preventDefault();
    localStorage.setItem(
      ALERT_LOCAL_STORAGE_KEY,
      JSON.stringify(this.messages),
    );
  };

  getMessages = () => {
    const text = localStorage.getItem(ALERT_LOCAL_STORAGE_KEY);
    if (!text) {
      return [];
    }

    return JSON.parse(text) || [];
  };

  fetchData = () => {
    const { fetchSystemAlert, fetchPersonalAlert, user } = this.props;
    fetchSystemAlert();
    fetchPersonalAlert();
  };

  handleSubscribe = prevProps => {
    const { systemAlerts, personalAlerts } = this.props;
    if (systemAlerts !== prevProps.systemAlerts) {
      this.unsubscribeSystemAlert(prevProps);
      this.subscribeSystemAlert();
    }
    if (personalAlerts !== prevProps.personalAlerts) {
      this.unsubscribePersonalAlert(prevProps);
      this.subscribePersonalAlert();
    }
  };

  isShowNotification = settingSystem => {
    return (
      settingSystem &&
      settingSystem.dataSystem &&
      settingSystem.dataSystem.notificationSubscribed
    );
  };

  getSystemChannelName = item => {
    return `Notification.${item.alertCode}`;
  };

  getPersonalChannel = item => {
    return `Notification.${item.alertCode}_${item.organCode}`;
  };

  subscribePersonalAlert = () => {
    const { personalAlerts } = this.props;
    personalAlerts.forEach(item => {
      if (item.isActive && item.notification) {
        messageHub.subscribe(
          this.getPersonalChannel(item),
          this.onReceiveAlert,
        );
      }
    });
  };

  unsubscribePersonalAlert = props => {
    const { personalAlerts } = props;
    personalAlerts.forEach(item => {
      if (item.isActive && item.notification) {
        messageHub.unsubscribe(
          this.getPersonalChannel(item),
          this.onReceiveAlert,
        );
      }
    });
  };

  subscribeSystemAlert = () => {
    const { systemAlerts } = this.props;
    systemAlerts.forEach(item => {
      if (item.subscribed && item.groupSubscribed) {
        messageHub.subscribe(
          this.getSystemChannelName(item),
          this.onReceiveAlert,
        );
      }
    });
  };

  unsubscribeSystemAlert = props => {
    const { systemAlerts } = props;
    systemAlerts.forEach(item => {
      if (item.subscribed && item.groupSubscribed) {
        messageHub.unsubscribe(
          this.getSystemChannelName(item),
          this.onReceiveAlert,
        );
      }
    });
  };

  updateMessages = messages => {
    this.messages = messages;
  };

  onReceiveAlert = message => {
    try {
      const now = new Date().getTime();
      const {
        updateNotificationList,
        alert,
        settingSystem,
        appendAlertHistory,
      } = this.props;
      updateNotificationList(message);
      appendAlertHistory(message);
      if (!this.isShowNotification(settingSystem)) {
        return;
      }
      if (
        now > this.lastShowTime + DELAY_SHOW_MESSAGE &&
        !this.messages.length
      ) {
        const { locale } = this.props;
        this.lastShowTime = now;
        alert.show({
          ...message,
          message: getAlertMessage(message, locale),
          title: getAlertTitle(message, locale),
        });
        return;
      }
      this.updateMessages(this.messages.concat(message));
      if (this.intervalShowMessage) {
        return;
      }
      this.intervalShowMessage = setInterval(() => {
        const { locale } = this.props;
        const now = new Date().getTime();
        const [first, ...rest] = this.messages;
        alert.show({
          ...first,
          message: getAlertMessage(first, locale),
          title: getAlertTitle(first, locale),
        });
        this.lastShowTime = now;
        if (!rest.length) {
          clearInterval(this.intervalShowMessage);
          this.intervalShowMessage = null;
        }
        this.updateMessages(rest);
      }, DELAY_SHOW_MESSAGE);
    } catch (e) {
      console.log(e);
    }
  };

  render() {
    return null;
  }
}

AlertSubscriber.propTypes = {
  systemAlerts: PropTypes.array.isRequired,
  personalAlerts: PropTypes.array.isRequired,
  fetchSystemAlert: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  alert: PropTypes.object.isRequired,
  settingSystem: PropTypes.object,
};

const mapDispatchToProps = {
  fetchSystemAlert,
  fetchPersonalAlert,
  updateNotificationList,
  appendAlertHistory,
};

const mapStateToProps = state => ({
  locale: state.i18n.locale,
  systemAlerts: state[REDUCER_NAME].systemAlerts,
  personalAlerts: state[REDUCER_NAME].personalAlerts,
  settingSystem: state[SIDEBAR_REDUCER].settingSystem,
  user: state.userInfo,
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withAlert(),
)(AlertSubscriber);
